import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import classes from '../components/common/Toast.module.css';

const ToastContext = createContext(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((message, type = 'success', duration = 8000) => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts(prev => [...prev, { id, message, type, duration }]);

        if (duration !== Infinity) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    }, [removeToast]);

    const getIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle2 size={20} color="#10b981" />;
            case 'error': return <AlertCircle size={20} color="#f87171" />;
            case 'warning': return <AlertTriangle size={20} color="#fbbf24" />;
            case 'info': return <Info size={20} color="#60a5fa" />;
            default: return null;
        }
    };

    const getToastClass = (type) => {
        switch (type) {
            case 'success': return classes.toastSuccess;
            case 'error': return classes.toastError;
            case 'warning': return classes.toastWarning;
            case 'info': return '';
            default: return '';
        }
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className={classes.toastContainer}>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`${classes.toast} ${getToastClass(toast.type)}`}
                        onClick={() => removeToast(toast.id)}
                    >
                        <div className={classes.iconContainer}>
                            {getIcon(toast.type)}
                        </div>
                        <div className={classes.message}>{toast.message}</div>
                        <button
                            className={classes.closeButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeToast(toast.id);
                            }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
